import { z } from 'zod';
import { defineTool, ADMINS, STAFF, EVERYONE } from './define.js';
import { PendingActionSchema } from '../types/session.js';
import { TenantSchema, TenantSettingsSchema, UserPreferencesSchema, UserSchema, ProviderSchema } from '../types/tenant.js';
import { RoleSchema } from '../types/common.js';
import { UsageMetricSchema } from '../types/audit.js';
import { DocumentSchema } from '../types/clinical.js';

const STAFF_AND_PATIENT = [...STAFF, 'PATIENT'] as const;

// ---------------------------------------------------------------- Pending actions (generic)
export const getPendingActions = defineTool({
  name: 'getPendingActions',
  description: 'List unconfirmed drafts for this session (orders, prescriptions, appointments, notes, registrations).',
  input: z.object({}),
  output: z.object({ pendingActions: z.array(PendingActionSchema) }),
  roles: STAFF_AND_PATIENT, requiresPatientContext: false, write: false, exposeToLlm: true,
});

export const confirmPendingAction = defineTool({
  name: 'confirmPendingAction',
  description: 'Generic confirmation of any pending action by id when the user says "yes, confirm". Prefer the typed confirm tools when you know the kind. Idempotent.',
  input: z.object({ pendingActionId: z.string(), acknowledgedFindingIds: z.array(z.string()).default([]) }),
  output: z.object({ pendingAction: PendingActionSchema, result: z.record(z.string(), z.unknown()), replayed: z.boolean() }),
  roles: STAFF_AND_PATIENT, requiresPatientContext: false, write: true, exposeToLlm: true,
});

export const rejectPendingAction = defineTool({
  name: 'rejectPendingAction',
  description: 'Discard a pending action when the user cancels or says no. Releases any held appointment slot.',
  input: z.object({ pendingActionId: z.string(), reason: z.string().max(200).optional() }),
  output: z.object({ pendingAction: PendingActionSchema }),
  roles: STAFF_AND_PATIENT, requiresPatientContext: false, write: true, exposeToLlm: true, audit: 'PENDING_REJECT',
});

// ---------------------------------------------------------------- Preferences
export const updateMyPreferences = defineTool({
  name: 'updateMyPreferences',
  description: 'Update the caller\'s own preferences: voice output on/off, preferred voice, language, listening mode, shortcut, silence threshold.',
  input: UserPreferencesSchema.partial(),
  output: z.object({ preferences: UserPreferencesSchema }),
  roles: EVERYONE, requiresPatientContext: false, write: true, exposeToLlm: true, audit: 'USER_UPDATE',
});

// ---------------------------------------------------------------- Tenant administration (API only)
export const provisionTenant = defineTool({
  name: 'provisionTenant',
  description: 'System admin: create a tenant with its first tenant administrator.',
  input: z.object({
    name: z.string().min(2),
    nameAr: z.string().optional(),
    settings: TenantSettingsSchema.partial().optional(),
    admin: z.object({ email: z.string().email(), displayName: z.string().min(1) }),
  }),
  output: z.object({ tenant: TenantSchema, admin: UserSchema }),
  roles: ['SYSTEM_ADMIN'], requiresPatientContext: false, write: true, exposeToLlm: false, audit: 'TENANT_PROVISION',
});

export const getTenant = defineTool({
  name: 'getTenant',
  description: 'Tenant profile and settings.',
  input: z.object({}),
  output: z.object({ tenant: TenantSchema }),
  roles: STAFF_AND_PATIENT, requiresPatientContext: false, write: false, exposeToLlm: false,
});

export const updateTenantSettings = defineTool({
  name: 'updateTenantSettings',
  description: 'Tenant admin: update clinic settings (time zone, appointment types, voice defaults, specialty configuration, CDS rule packs).',
  input: z.object({ settings: TenantSettingsSchema.deepPartial() }),
  output: z.object({ tenant: TenantSchema }),
  roles: ADMINS, requiresPatientContext: false, write: true, exposeToLlm: true, audit: 'TENANT_UPDATE',
});

export const createUser = defineTool({
  name: 'createUser',
  description: 'Tenant admin: create a staff or patient user. Creates the Cognito identity when an identity provider is configured.',
  input: z.object({
    email: z.string().email(),
    displayName: z.string().min(1),
    displayNameAr: z.string().optional(),
    roles: z.array(RoleSchema).min(1),
    patientId: z.string().optional(),
    provider: z.object({ specialty: z.string().optional() }).optional().describe('Create a schedulable provider profile'),
  }),
  output: z.object({ user: UserSchema, provider: ProviderSchema.optional(), temporaryPassword: z.string().optional() }),
  roles: ADMINS, requiresPatientContext: false, write: true, exposeToLlm: false, audit: 'USER_CREATE',
});

export const listUsers = defineTool({
  name: 'listUsers',
  description: 'Tenant admin: list users.',
  input: z.object({ role: RoleSchema.optional() }),
  output: z.object({ users: z.array(UserSchema) }),
  roles: ADMINS, requiresPatientContext: false, write: false, exposeToLlm: false,
});

export const upsertProviderSchedule = defineTool({
  name: 'upsertProviderSchedule',
  description: 'Tenant admin or the provider: set the weekly availability template.',
  input: z.object({
    providerId: z.string(),
    weekly: z.record(z.string(), z.array(z.object({ start: z.string(), end: z.string(), slotMinutes: z.number().int().default(15), appointmentTypeCodes: z.array(z.string()).optional() }))),
  }),
  output: z.object({ ok: z.literal(true) }),
  roles: [...ADMINS, 'PHYSICIAN'], requiresPatientContext: false, write: true, exposeToLlm: true, audit: 'TENANT_UPDATE',
});

export const getUsage = defineTool({
  name: 'getUsage',
  description: 'Tenant usage metrics for billing: LLM tokens, transcription milliseconds, Polly characters, API requests.',
  input: z.object({ from: z.string(), to: z.string() }),
  output: z.object({ metrics: z.array(UsageMetricSchema) }),
  roles: ADMINS, requiresPatientContext: false, write: false, exposeToLlm: true,
});

// ---------------------------------------------------------------- Documents
export const createDocumentUpload = defineTool({
  name: 'createDocumentUpload',
  description: 'Register a document for the active patient and return a short-lived presigned S3 PUT URL. The document becomes AVAILABLE after upload completes.',
  input: z.object({ title: z.string(), category: DocumentSchema.shape.category, contentType: z.string(), sizeBytes: z.number().int().positive().max(50 * 1024 * 1024) }),
  output: z.object({ document: DocumentSchema, uploadUrl: z.string().url(), expiresAt: z.string() }),
  roles: STAFF, requiresPatientContext: true, write: true, exposeToLlm: true, audit: 'DOCUMENT_UPLOAD',
});

export const listDocuments = defineTool({
  name: 'listDocuments',
  description: 'Documents attached to the active patient record.',
  input: z.object({ category: DocumentSchema.shape.category.optional() }),
  output: z.object({ documents: z.array(DocumentSchema) }),
  roles: STAFF_AND_PATIENT, requiresPatientContext: true, write: false, exposeToLlm: true, audit: 'RECORD_VIEW',
});

export const getDocumentUrl = defineTool({
  name: 'getDocumentUrl',
  description: 'Short-lived presigned GET URL for one document. Never a public link.',
  input: z.object({ documentId: z.string() }),
  output: z.object({ url: z.string().url(), expiresAt: z.string() }),
  roles: STAFF_AND_PATIENT, requiresPatientContext: true, write: false, exposeToLlm: true, audit: 'DOCUMENT_VIEW',
});
